import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { getPermissions, fetchUserPermissions, getUser, hasUsablePermissionData } from '../services/auth';
import {
  hasActionForResource,
  hasResourceAccess,
  getActionsForResource,
  getPermissionDetails,
} from '../utils/resourceMapping';

const RBACContext = createContext();

const summarizePermissions = (permissions = []) => ({
  count: Array.isArray(permissions) ? permissions.length : 0,
  missingResource: Array.isArray(permissions) ? permissions.filter((perm) => !perm?.resource).length : 0,
  missingAction: Array.isArray(permissions) ? permissions.filter((perm) => !perm?.action).length : 0,
  resources: Array.isArray(permissions)
    ? [...new Set(permissions.map((perm) => perm?.resource?.displayName || perm?.resource?.resourceKey).filter(Boolean))]
    : [],
  actions: Array.isArray(permissions)
    ? [...new Set(permissions.map((perm) => perm?.action?.name).filter(Boolean))]
    : [],
});

export const RBACProvider = ({ children }) => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch permissions for the logged-in user
  const loadPermissions = useCallback(async () => {
    try {
      setLoading(true);
      const user = getUser();
      if (user?.userId) {
        console.log('[RBACContext] Loading permissions for user:', user.userId);
        const fetchedPermissions = await fetchUserPermissions(user.userId);
        console.log('[RBACContext] Permissions loaded:', summarizePermissions(fetchedPermissions));
        setPermissions(fetchedPermissions);
        setError(null);
      } else {
        console.log('[RBACContext] No user found, clearing permissions');
        setPermissions([]);
      }
    } catch (err) {
      console.error('Failed to load permissions:', err);
      setError(err.message);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize permissions on mount only
  useEffect(() => {
    const initializePermissions = async () => {
      try {
        const user = getUser();

        if (user?.userId) {
          const cachedPerms = getPermissions();
          console.log('[RBACProvider] Initializing permissions', {
            userId: user.userId,
            cached: summarizePermissions(cachedPerms || []),
          });

          if (cachedPerms?.length > 0 && hasUsablePermissionData(cachedPerms)) {
            console.log('[RBACProvider] Using usable cached permissions', summarizePermissions(cachedPerms));
            setPermissions(cachedPerms);
          } else {
            if (cachedPerms?.length > 0) {
              console.warn('[RBACProvider] Cached permissions are stale or missing resource/action details; refetching', summarizePermissions(cachedPerms));
            }
            const freshPerms = await fetchUserPermissions(user.userId);
            if (freshPerms && freshPerms.length > 0) {
              console.log('[RBACProvider] Loaded fresh permissions', summarizePermissions(freshPerms));
              setPermissions(freshPerms);
            } else {
              console.warn('[RBACProvider] No permissions loaded for user', user.userId);
              setPermissions([]);
            }
          }
        } else {
          console.log('[RBACProvider] No user, clearing permissions');
          setPermissions([]);
        }
      } catch (err) {
        console.error('RBACProvider: Error initializing permissions:', err);
      }
    };

    initializePermissions();
  }, []);

  // Check if user has permission for a specific resource and action
  const hasPermission = useCallback((resourceId, actionId, scopeId = null) => {
    if (!permissions || permissions.length === 0) {
      console.log('[RBACContext] hasPermission denied: no permissions loaded', { resourceId, actionId, scopeId });
      return false;
    }

    const result = permissions.some(perm => {
      const resourceMatch = perm.resource_id === resourceId;
      const actionMatch = perm.action_id === actionId;
      const scopeMatch = scopeId === null || perm.scope_id === scopeId;

      return resourceMatch && actionMatch && scopeMatch;
    });
    console.log('[RBACContext] hasPermission result', { resourceId, actionId, scopeId, result });
    return result;
  }, [permissions]);

  // Check if user has permission for a resource by resource key
  const hasResourcePermission = useCallback((resourceKey, action) => {
    if (!permissions || permissions.length === 0) {
      console.log('[RBACContext] hasResourcePermission denied: no permissions loaded', { resourceKey, action });
      return false;
    }

    const result = permissions.some(perm => {
      const resourceMatch = String(perm.resource?.resourceKey || '').trim().toLowerCase() === String(resourceKey || '').trim().toLowerCase();
      const actionMatch = String(perm.action?.name || '').trim().toLowerCase() === String(action || '').trim().toLowerCase();
      return resourceMatch && actionMatch;
    });
    console.log('[RBACContext] hasResourcePermission result', { resourceKey, action, result });
    return result;
  }, [permissions]);

  // Check if user has permission for a resource by displayName
  const hasResourcePermissionByName = useCallback((displayName, actionName) => {
    return hasActionForResource(permissions, displayName, actionName);
  }, [permissions]);

  // Check if user has any access to a resource by displayName
  const canAccessResource = useCallback((displayName) => {
    return hasResourceAccess(permissions, displayName);
  }, [permissions]);

  // Get all actions for a resource by displayName
  const getActionsForResourceByName = useCallback((displayName) => {
    return getActionsForResource(permissions, displayName);
  }, [permissions]);

  // Get permission details for a resource and action
  const getPermissionDetailsForAction = useCallback((displayName, actionName) => {
    return getPermissionDetails(permissions, displayName, actionName);
  }, [permissions]);

  // Get all resources the user has access to
  const getAccessibleResources = useCallback(() => {
    if (!permissions || permissions.length === 0) return [];

    const resourceMap = new Map();
    permissions.forEach(perm => {
      if (perm.resource) {
        const key = perm.resource.id;
        if (!resourceMap.has(key)) {
          resourceMap.set(key, {
            id: perm.resource.id,
            key: perm.resource.resourceKey,
            type: perm.resource.resourceType,
            displayName: perm.resource.displayName,
            actions: []
          });
        }
        if (perm.action && !resourceMap.get(key).actions.includes(perm.action.name)) {
          resourceMap.get(key).actions.push(perm.action.name);
        }
      }
    });

    return Array.from(resourceMap.values());
  }, [permissions]);

  // Get all actions available for a specific resource
  const getResourceActions = useCallback((resourceId) => {
    if (!permissions || permissions.length === 0) return [];

    const actionsSet = new Set();
    permissions.forEach(perm => {
      if (perm.resource_id === resourceId && perm.action) {
        actionsSet.add(perm.action.name);
      }
    });

    return Array.from(actionsSet);
  }, [permissions]);

  const value = {
    permissions,
    loading,
    error,
    loadPermissions,
    hasPermission,
    hasResourcePermission,
    hasResourcePermissionByName,
    canAccessResource,
    getActionsForResourceByName,
    getPermissionDetailsForAction,
    getAccessibleResources,
    getResourceActions,
  };

  return (
    <RBACContext.Provider value={value}>
      {children}
    </RBACContext.Provider>
  );
};

export const useRBAC = () => {
  const context = useContext(RBACContext);
  if (!context) {
    throw new Error('useRBAC must be used within RBACProvider');
  }
  return context;
};

export default RBACContext;
